import { motion } from "framer-motion";
import { Users, HeartHandshake, Compass, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const ICONS = [Users, HeartHandshake, Compass, ShieldCheck];

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1527142879-95b61a0b8226?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHx3b21lbiUyMGdyb3VwJTIwdHJhdmVsJTIwZnJpZW5kcyUyMGx1eHVyeSUyMGdldGF3YXl8ZW58MHx8fHwxNzkwNjEwMDU1fDA&ixlib=rb-4.1.0&q=60&w=1200";
const LAZY = { loading: "lazy", decoding: "async" };

export default function AboutFaqs() {
  const { t } = useLang();
  const stats = t("about.stats");
  const values = t("about.values");

  return (
    <section id="nosotros" data-testid="about-section" className="py-24 sm:py-32 bg-[#FAF6EE]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="md:col-span-7 bg-[#0B2E4A] text-[#FAF6EE] rounded-3xl p-8 sm:p-12 flex flex-col justify-between"
          >
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D9A854] mb-4">{t("about.eyebrow")}</p>
              <h2 className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-balance">
                {t("about.title")}
              </h2>
              <p className="mt-6 text-[#F1E7D6]/75 text-base sm:text-lg leading-relaxed max-w-xl">{t("about.text")}</p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {Array.isArray(stats) && stats.map(([n, l]) => (
                <div key={l}>
                  <div className="font-display font-semibold text-3xl sm:text-4xl text-[#D9A854]">{n}</div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#F1E7D6]/70 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="md:col-span-5 rounded-3xl overflow-hidden relative min-h-[320px]"
          >
            <img src={ABOUT_IMG} alt="Mujeres compartiendo un viaje" className="absolute inset-0 w-full h-full object-cover" {...LAZY} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B2E4A]/70 to-transparent" />
            <p className="absolute bottom-6 left-6 right-6 text-[#FAF6EE] font-display font-semibold text-xl leading-snug">
              {t("about.quote")}
            </p>
          </motion.div>

          {Array.isArray(values) && values.map((v, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="md:col-span-3 bg-white rounded-3xl border border-[#E6DED1] p-7 shadow-[0_4px_20px_-2px_rgba(11,46,74,0.06)]"
              >
                <span className="w-11 h-11 rounded-full bg-[#D9A854]/10 flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#7A571C]" />
                </span>
                <h3 className="font-display font-semibold text-lg text-[#0B2E4A]">{v.title}</h3>
                <p className="text-sm text-[#56636D] mt-2 leading-relaxed">{v.text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
